import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { PlanFinePrint, StartFreeButton } from '@/components/marketing/free-plan-cta'
import { FREE_PLAN } from '@/lib/free-plan'
import { FREE_DOMAIN_RULE, PAID_ANY_EMAIL } from '@/lib/free-domain'

const POST_URL = 'https://uptrack.app/blog/freshping-discord-integration-alternative'
const TITLE = 'Freshping Discord Integration: Alerts After Shutdown'
const DESCRIPTION =
  'Freshping shut down on March 6, 2026. What its integrations were, and how to get uptime alerts into Discord now, on a free plan or through any webhook.'
const PUBLISHED = '2026-10-12'

const FAQ: { q: string; a: string }[] = [
  {
    q: 'Did Freshping have a Discord integration?',
    a: 'We found no Freshping page that lists Discord as a built-in integration. Third-party guides describe Slack, Microsoft Teams and a webhook option, and automation listings show Discord as a place Freshping alerts could be sent through a tool such as Zapier.',
  },
  {
    q: 'When did Freshping shut down?',
    a: 'Freshworks ended Freshping on March 6, 2026. As third parties quote the notice, access for the no-cost tier ended that day and paid plans ran until they expired. Freshworks kept the data for 90 days after the shutdown.',
  },
  {
    q: 'Can I get Discord alerts on a free plan?',
    a: `Yes, with Uptrack. The free plan has email plus one chat channel (Discord, Slack or Telegram), ${FREE_PLAN.monitors} monitors checked every ${FREE_PLAN.intervalSeconds} seconds from one location. ${FREE_DOMAIN_RULE} ${PAID_ANY_EMAIL}`,
  },
  {
    q: 'What happens if I delete the Discord webhook later?',
    a: 'Discord then answers that the webhook is unknown. Uptrack stops retrying, disables the channel after repeated fatal failures, and emails the account owner.',
  },
  {
    q: 'Can any monitoring tool post to a Discord webhook?',
    a: 'Only a tool that sends the message in the shape Discord expects: at least one of content, embeds, components, a file or a poll. A tool that sends its own JSON needs a small relay such as an automation tool or a short script in between.',
  },
]

export const Route = createFileRoute('/_site/blog/freshping-discord-integration-alternative')({
  component: FreshpingDiscordPage,
  head: () => ({
    meta: [
      { title: 'Freshping Discord Integration After Shutdown — Uptrack' },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: 'Freshping Discord Integration After Shutdown — Uptrack' },
      {
        property: 'og:description',
        content: 'Freshping is gone. Here is how to send uptime alerts to Discord now, with a free chat channel or any webhook.',
      },
      { property: 'og:image', content: 'https://uptrack.app/og/blog-freshping-discord-integration-alternative.png' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: POST_URL },
      { property: 'article:published_time', content: PUBLISHED },
    ],
    links: [{ rel: 'canonical', href: POST_URL }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: TITLE,
          description: DESCRIPTION,
          datePublished: PUBLISHED,
          dateModified: PUBLISHED,
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: {
            '@type': 'Organization',
            name: 'Uptrack',
            url: 'https://uptrack.app',
            logo: { '@type': 'ImageObject', url: 'https://uptrack.app/uptrack-logo-header.png' },
          },
          mainEntityOfPage: POST_URL,
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQ.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }),
      },
    ],
  }),
})

const p = 'mb-4 text-lg leading-[1.7] text-body'
const link = 'text-primary hover:underline'
const list = 'mb-4 list-disc space-y-2 pl-6 text-lg leading-[1.7] text-body'
const steps = 'mb-4 list-decimal space-y-2 pl-6 text-lg leading-[1.7] text-body'
const h2 = 'font-sans mb-4 text-2xl font-semibold'

const COMPARE: { tool: string; free: string; faster: string }[] = [
  { tool: 'Uptrack', free: '5 monitors, 30 s checks, one location, email plus one chat channel', faster: '30 s on every plan' },
  { tool: 'UptimeRobot', free: '50 monitors, 5 min checks', faster: '30 s starts on Team at $41/mo ($35 yearly)' },
  { tool: 'StatusCake', free: '10 monitors, 5 min checks', faster: '30 s starts on Business at $79.99/mo ($66.66 yearly)' },
  { tool: 'Better Stack', free: '10 monitors and 10 heartbeats, 3 min checks', faster: '30 s needs a responder seat at $34/mo ($29 yearly)' },
  { tool: 'HetrixTools', free: '15 monitors, 1 min checks', faster: 'Every plan checks every minute' },
]

function FreshpingDiscordPage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <p className="mb-3 text-sm text-muted-foreground">October 12, 2026</p>
      <h1 className="font-sans mb-6 text-4xl font-semibold">Freshping Discord integration: how to get the alerts back</h1>

      <section className="mb-12">
        <p className={p}>
          If you search for a Freshping Discord integration today, you land on pages for a tool that no longer exists. Freshworks
          shut Freshping down on March 6, 2026. The monitors stopped, and the alerts that used to ping your team stopped with them.
        </p>
        <p className={p}>
          This post covers three things: what Freshping&apos;s integrations really were, how to rebuild the same alert in Discord
          in about ten minutes, and what to check before you pick a replacement. We make Uptrack, so we show our own setup first
          and say where it is not the right choice.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>What happened to Freshping</h2>
        <p className={p}>
          Freshworks said that supporting Freshping was no longer part of its plan and ended the product. As third parties quote the
          notice, the no-cost tier ended on March 6, 2026, and paid plans were not renewed after that date and ran until they expired.
          Freshworks kept the data for 90 days after the shutdown. Users of the no-cost tier had until about June 4, 2026 to export, so
          that window is closed now.
          We could not load Freshworks&apos; own notice. The dates come from{' '}
          <a className={link} href="https://notifier.so/guides/freshping-shutdown/" rel="noopener noreferrer">
            a shutdown guide by Notifier
          </a>{' '}
          and from{' '}
          <a className={link} href="https://www.lowendspirit.com/discussion/10419/freshping-discontinued-from-6-march-2026-or-when-payment-runs-out" rel="noopener noreferrer">
            a forum thread that quotes the notice
          </a>
          . Freshworks has given no sign that Freshping is coming back.
        </p>
        <p className={p}>
          The practical result: you have to rebuild your monitors somewhere else. The export is gone, so write down your URLs from
          your status page, your DNS records or your deploy notes before you forget them.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>What &quot;Freshping integrations&quot; meant</h2>
        <p className={p}>
          Freshping had no public integration page that we could find. Third-party guides describe three ways to get an alert out:
          email, Slack, and a webhook field under Settings, Integrations. Automation marketplaces such as Zapier also list Microsoft
          Teams and Discord among the places a Freshping alert could go.
        </p>
        <p className={p}>
          That matters because the phrase &quot;Freshping Discord integration&quot; suggests a built-in button. We found no sign of
          one. Teams that had Freshping alerts in Discord almost certainly used the webhook field or an automation tool in between.
          If that was you, you only need the same thing again: a monitor, a rule that says &quot;alert after a failed check&quot;, and
          a Discord channel that can receive it.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>Option 1: Discord alerts with Uptrack, step by step</h2>
        <p className={p}>
          The free plan checks <strong>{FREE_PLAN.monitors} monitors every {FREE_PLAN.intervalSeconds} seconds</strong> from one
          location ({FREE_PLAN.location}). It includes email plus <strong>one chat channel</strong>: Discord, Slack or Telegram. {FREE_DOMAIN_RULE}{' '}
          {PAID_ANY_EMAIL}
        </p>
        <ol className={steps}>
          <li>
            <Link to="/signup" search={{ plan: 'free' }} className={link}>
              Create a free account
            </Link>{' '}
            and add your first monitor. Use the URL you used in Freshping.
          </li>
          <li>
            Open <strong>Alerts</strong> in the dashboard and press <strong>Connect Discord</strong>. Uptrack sends you to Discord, where you
            pick the server and the channel. Discord creates an incoming webhook for that channel, and Uptrack stores it as an alert
            channel. You never copy a URL.
          </li>
          <li>
            Prefer to do it by hand? In Discord, open the channel settings, then Integrations, then Webhooks, and create a new webhook.
            Copy its URL. In Uptrack, add an alert channel of type Discord and paste the URL. It must start with{' '}
            <code>https://discord.com/api/webhooks/</code>. Discord&apos;s{' '}
            <a className={link} href="https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks" rel="noopener noreferrer">
              intro to webhooks
            </a>{' '}
            shows the menus, which can change.
          </li>
          <li>Attach the channel to your monitors. A down alert and a recovery alert both go to it.</li>
        </ol>
        <p className={p}>
          The alert is a Discord embed, not a plain line of text. Down alerts are red, recoveries are green, and a degraded warning is
          yellow. The fields show the monitor name, the URL, the status, when it started, how many checks failed and, when we know it, the
          reason: a TLS failure, a 503, a timeout. A link opens the incident. A recovery reads &quot;is back UP&quot; with the time it
          resolved.
        </p>
        <p className={p}>
          If you later delete the webhook in Discord, Discord answers that it is unknown. Uptrack stops retrying, disables that channel
          after repeated fatal failures, and emails the account owner, so a deleted channel does not fail in silence.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>Option 2: any monitoring tool with a webhook</h2>
        <p className={p}>
          You can keep another tool and still read its alerts in Discord. The catch is the message shape. Discord&apos;s{' '}
          <a className={link} href="https://docs.discord.com/developers/resources/webhook" rel="noopener noreferrer">
            execute webhook endpoint
          </a>{' '}
          wants a JSON body with at least one of <code>content</code>, <code>embeds</code>, <code>components</code>, a file or a poll. A
          monitoring tool that posts its own JSON, with fields like <code>monitor</code> and <code>status</code>, will be rejected or
          show nothing useful.
        </p>
        <p className={p}>So there are two routes:</p>
        <ul className={list}>
          <li>
            Use a tool that has a Discord channel type, as above. This is the simplest, and nothing sits between your monitor and the
            channel.
          </li>
          <li>
            Put a relay in between. An automation tool or a ten-line script receives the tool&apos;s webhook and posts to Discord. A
            minimal post looks like this:
          </li>
        </ul>
        <pre tabIndex={0} className="mb-4 overflow-x-auto rounded-lg border border-border bg-card p-4 text-sm leading-relaxed">
          <code>{`curl -X POST "$DISCORD_WEBHOOK_URL" \\
  -H "Content-Type: application/json" \\
  -d '{"content": "api.example.com is DOWN (503)"}'`}</code>
        </pre>
        <p className={p}>
          Know the limits before you build on it. A message body is capped at 2,000 characters, one message can carry up to 10 embeds,
          and a webhook can send 30 messages per minute. A flapping monitor behind a relay can hit that last limit during a real
          outage, which is when you most need the alerts. Alert confirmation, where several failed checks are needed before a message
          goes out, protects both your channel and your sleep.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>How the free options compare</h2>
        <p className={p}>
          All numbers come from each vendor&apos;s public pricing page, checked on October 12, 2026. We have not tested the Discord
          alerts of the other tools, so open their integration lists before you move. Our own{' '}
          <Link to="/compare/uptimerobot" className={link}>
            UptimeRobot comparison
          </Link>{' '}
          and the{' '}
          <Link to="/alternatives/freshping" className={link}>
            Freshping alternatives page
          </Link>{' '}
          go deeper.
        </p>
        <div className="mb-4 overflow-x-auto" tabIndex={0} role="region" aria-label="Free plans compared">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Tool</th>
                <th className="pb-3 px-3 text-left font-medium text-muted-foreground">Free plan</th>
                <th className="pb-3 pl-3 text-left font-medium text-muted-foreground">30 second checks</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((row) => (
                <tr key={row.tool} className="border-b last:border-0">
                  <td className="py-3 pr-4 font-medium">{row.tool}</td>
                  <td className="py-3 px-3 text-body">{row.free}</td>
                  <td className="py-3 pl-3 text-body">{row.faster}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={p}>
          Freshping&apos;s own free plan monitored 50 URLs at 1-minute intervals, as third-party listings describe it. None of the
          options above matches both numbers on a free plan. Pick the one that fits what you give up more easily: monitor count or
          check speed.
        </p>
      </section>

      <section className="mb-12">
        <h2 className={h2}>Move your monitors without losing a day</h2>
        <ol className={steps}>
          <li>List every URL you monitored. Include health endpoints, not only the home page.</li>
          <li>Create the monitors in the new tool at the same interval, or faster. Note which ones need a keyword check, not only a status code.</li>
          <li>Connect Discord and attach the channel to every monitor.</li>
          <li>
            Test the whole path once. Point a temporary monitor at a URL that returns 404 on purpose, wait for the alert, then delete the
            monitor. You will see the real message format and learn whether the channel works.
          </li>
          <li>Decide who reads the Discord channel at night. A channel nobody watches is a log, not an alert.</li>
        </ol>
      </section>

      <section className="mb-12">
        <h2 className={h2}>When Uptrack is not for you</h2>
        <ul className={list}>
          <li>
            You need many free monitors. UptimeRobot&apos;s free plan has 50 and HetrixTools has 15. Ours has {FREE_PLAN.monitors}.
          </li>
          <li>You need SMS or phone-call alerts on the free plan. Webhook, SMS and phone alerts are on Pro.</li>
          <li>
            You want more than one chat channel on the free plan. Free has one: Discord, Slack or Telegram. Use email for the second
            route.
          </li>
          <li>
            You signed up with an address on a free mail provider and want the free plan. {FREE_DOMAIN_RULE} You can add a work email
            address to your account in Settings, or choose a paid plan, which accepts any email address.
          </li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className={h2}>FAQ</h2>
        <div className="space-y-6">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="font-sans mb-1 text-lg font-semibold">{f.q}</h3>
              <p className="text-lg leading-[1.7] text-body">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12 rounded-lg border border-border bg-muted p-5" lang="de">
        <h2 className="font-sans mb-3 text-xl font-semibold">Kurzfassung auf Deutsch</h2>
        <p className="text-lg leading-[1.7] text-body">
          Freshping wurde am 6. März 2026 eingestellt. Wenn Sie Ausfallmeldungen in Discord brauchen, legen Sie bei Uptrack ein
          kostenloses Konto an und verbinden Sie Discord unter „Alerts“. Die kostenlose Version prüft bis zu 5 Monitore alle 30
          Sekunden von einem Standort in Nürnberg aus und enthält E-Mail plus einen Chat-Kanal (Discord, Slack oder Telegram). Sie
          braucht eine geschäftliche E-Mail-Adresse. Bezahlte Tarife akzeptieren jede Adresse.
        </p>
      </section>

      <div className="rounded-lg border border-border bg-muted p-8 text-center">
        <h2 className="font-sans mb-3 text-xl font-semibold">Get your alerts back in Discord</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">Five monitors, checked every 30 seconds, with email and one chat channel.</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <StartFreeButton />
          <Button size="lg" variant="outline" className="h-11 px-5 text-[15px]" asChild>
            <Link to="/pricing">See plans</Link>
          </Button>
        </div>
        <PlanFinePrint className="mt-4" />
      </div>
    </article>
  )
}
